import { useState, useEffect } from 'react'
import { Dialog, DialogBody, DialogContent, DialogHeader, DialogTitle } from '@cutilei/ui'
import cutileiApi from 'services/cutileiApi'
import PayoutService from 'services/Payout'
import { logApiError } from 'services/apiError'
import payoutResolutionValidator from 'validators/payoutResolutionValidator'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import Select from 'components/Inputs/Select'
import { ReactComponent as Loading } from 'icons/loading2.svg'
import { Formik } from 'formik'
import { FaExclamationTriangle } from 'react-icons/fa'
import { DateTime } from 'luxon'

import {
  Container,
  Section,
  SectionTitle,
  Actions,
  ReviewCard,
  ErrorCard
} from './styles'

import {
  DataList,
  DataRow,
  DataLabel,
  DataValue,
  StatusBadge,
  TextArea,
  Column,
  FieldLabel,
  InfoText,
  WarningText
} from '../../styles'

const OUTCOME_OPTIONS = [
  {value: 'paid', label: 'A transferência saiu (quitar)'},
  {value: 'canceled', label: 'A transferência não saiu (encerrar)'}
]

const formatDate = value => {
  if (!value) return '—'

  const date = DateTime.fromISO (value)

  return date.isValid ? date.toFormat ('dd/MM/yyyy HH:mm') : '—'
}

const formatDay = value => {
  if (!value) return '—'

  const date = DateTime.fromISO (value)

  return date.isValid ? date.toFormat ('dd/MM/yyyy') : '—'
}

/*
  Um recebível visto pelo financeiro, com as duas únicas escritas que um administrador tem
  neste domínio. Ele pode encerrar uma obrigação; nunca criar uma, nunca antecipar a de outra
  pessoa.

  As duas ações respondem a perguntas diferentes, e é por isso que a API as separa em rotas:

  - **Cancelar** — "esta obrigação não deveria existir": atendimento validado por engano,
    fraude, acerto já feito por fora.
  - **Resolver** — "esta obrigação teve um desfecho que a máquina não conseguiu observar". É a
    saída do recebível que travou porque a chamada ao Asaas ficou sem resposta e a conciliação
    desistiu sem achar a transferência.

  Misturar os dois faria o relatório confundir erro de cadastro com falha de integração, que
  são problemas de times diferentes.
*/
export default function PayoutDetailsModal ({
  visible,
  payout: initialPayout,
  requestConfig,
  onUpdate = () => {},
  onClose: handleClose = () => {}
}) {
  const [loading, setLoading] = useState (true)
  const [payout, setPayout] = useState (initialPayout)
  const [error, setError] = useState (null)
  /* `null`, `'cancel'` ou `'resolve'`. Qual formulário está aberto. */
  const [action, setAction] = useState (null)

  useEffect (() => {
    getData ()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPayout?.id])

  /*
    A linha da lista não traz `anticipable` nem `cancelable` — eles só vêm no detalhe, porque
    dependem de regras que o servidor avalia. Sem esta leitura, os botões apareceriam pela
    situação, e "cancelar" ficaria de pé num recebível com transferência em voo.
  */
  const getData = async () => {
    try {
      const { data } = await cutileiApi.get (
        `/admin/payouts/${initialPayout.id}`, requestConfig
      )

      setPayout (PayoutService.normalize (data))
      setError (null)
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível carregar o recebível')
    } finally {
      setLoading (false)
    }
  }

  const submit = async (values, { setSubmitting }) => {
    try {
      setSubmitting (true)
      setError (null)

      const path = action === 'resolve' ? 'resolve' : 'cancel'
      const body = action === 'resolve'
        ? { outcome: values.outcome, reason: values.reason }
        : { reason: values.reason }

      const { data } = await cutileiApi.post (
        `/admin/payouts/${payout.id}/${path}`, body, requestConfig
      )

      const updated = PayoutService.normalize (data)

      setPayout (prev => ({ ...prev, ...updated }))
      onUpdate (updated)
      setAction (null)
    } catch (error) {
      logApiError (error)
      setError (error.response?.data?.message ?? 'Não foi possível concluir a operação')
    } finally {
      setSubmitting (false)
    }
  }

  const commissionPercentage = PayoutService.toPercentage (payout?.commissionPercentage)
  const needsReview = payout?.status === 'failed'

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>{payout?.business?.name ?? 'Recebível'}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Container>

            {loading ? (
              <Loading style={{alignSelf: 'center', margin: '20px 0px'}}/>
            ) : (
              <>
                <StatusBadge color={PayoutService.getStatusColor (payout.status)}>
                  {PayoutService.getStatusLabel (payout.status)}
                </StatusBadge>

                <Section>
                  <SectionTitle>Valores</SectionTitle>
                  <DataList>
                    <DataRow>
                      <DataLabel>Titular</DataLabel>
                      <DataValue>{PayoutService.getUserTypeLabel (payout.userType)}</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>Preço do atendimento</DataLabel>
                      <DataValue>{PayoutService.formatMoney (payout.grossAmount)}</DataValue>
                    </DataRow>
                    {commissionPercentage !== null && (
                      <DataRow>
                        <DataLabel>Comissão da manicure</DataLabel>
                        <DataValue>{`${commissionPercentage}%`}</DataValue>
                      </DataRow>
                    )}
                    <DataRow>
                      <DataLabel>Valor desta perna</DataLabel>
                      <DataValue>{PayoutService.formatMoney (payout.amount)}</DataValue>
                    </DataRow>
                    {payout.anticipated && (
                      <DataRow>
                        <DataLabel>
                          {`Taxa de antecipação (${Number (payout.feePercentage ?? 0)}%)`}
                        </DataLabel>
                        <DataValue>{PayoutService.formatMoney (payout.feeAmount)}</DataValue>
                      </DataRow>
                    )}
                    <DataRow>
                      <DataLabel>Transferido ao titular</DataLabel>
                      <DataValue>{PayoutService.formatMoney (payout.netAmount)}</DataValue>
                    </DataRow>
                  </DataList>
                </Section>

                <Section>
                  <SectionTitle>Datas</SectionTitle>
                  <DataList>
                    <DataRow>
                      <DataLabel>Atendimento</DataLabel>
                      <DataValue>{formatDay (payout.scheduleDate)}</DataValue>
                    </DataRow>
                    <DataRow>
                      <DataLabel>Vencimento</DataLabel>
                      <DataValue>{formatDate (payout.dueAt)}</DataValue>
                    </DataRow>
                    {/*
                      Antecipar move só a data em que a máquina paga, e deixa o vencimento
                      intocado — é a diferença entre as duas que mostra quantos dias a taxa comprou.
                    */}
                    <DataRow>
                      <DataLabel>Pagável a partir de</DataLabel>
                      <DataValue>{formatDate (payout.payableAt)}</DataValue>
                    </DataRow>
                    {payout.anticipatedAt && (
                      <DataRow>
                        <DataLabel>Antecipado em</DataLabel>
                        <DataValue>{formatDate (payout.anticipatedAt)}</DataValue>
                      </DataRow>
                    )}
                    {payout.paidAt && (
                      <DataRow>
                        <DataLabel>Pago em</DataLabel>
                        <DataValue>{formatDate (payout.paidAt)}</DataValue>
                      </DataRow>
                    )}
                    {payout.canceledAt && (
                      <DataRow>
                        <DataLabel>Cancelado em</DataLabel>
                        <DataValue>{formatDate (payout.canceledAt)}</DataValue>
                      </DataRow>
                    )}
                  </DataList>
                </Section>

                {payout.canceledReason && (
                  <Section>
                    <SectionTitle>Motivo do cancelamento</SectionTitle>
                    <InfoText>
                      {`${payout.canceledReason}`
                      + `${payout.canceledBy ? ` (${payout.canceledBy})` : ''}`}
                    </InfoText>
                  </Section>
                )}

                {payout.lastError && (
                  <ErrorCard>
                    <span>Último erro registrado</span>
                    <p>{payout.lastError}</p>
                  </ErrorCard>
                )}

                {needsReview && (
                  <ReviewCard>
                    <p>
                      <b>Este recebível precisa de conferência humana.</b>
                      {' Não foi possível saber se a transferência saiu — o dinheiro pode já estar '
                      + 'na conta do titular, e nada o paga enquanto isto não for resolvido. '
                      + 'Confira o extrato da conta Cutilei no Asaas antes de escolher: '
                      + 'transferência interna é instantânea e irreversível, e marcar como pago um '
                      + 'repasse que não saiu deixa o titular sem receber.'}
                    </p>
                  </ReviewCard>
                )}

                {error && <WarningText align='center'>{error}</WarningText>}

                {action ? (
                  <Formik
                    initialValues={{
                      resolving: action === 'resolve',
                      outcome: '',
                      reason: ''
                    }}
                    validationSchema={payoutResolutionValidator}
                    onSubmit={submit}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      isSubmitting,
                      setFieldValue,
                      setFieldTouched,
                      handleSubmit
                    }) => (
                      <Section>
                        <SectionTitle>
                          {action === 'resolve' ? 'Resolver' : 'Cancelar recebível'}
                        </SectionTitle>
                        {action === 'resolve' && (
                          <Column gap={5}>
                            <FieldLabel>O que o extrato do Asaas mostra?</FieldLabel>
                            <Select
                              name='outcome'
                              placeholder='Escolha o desfecho...'
                              options={OUTCOME_OPTIONS}
                              value={OUTCOME_OPTIONS.find (o => o.value === values.outcome) ?? ''}
                              error={errors.outcome}
                              touched={touched.outcome}
                              onChange={(name, option) => setFieldValue (name, option?.value ?? '')}
                              onBlur={setFieldTouched}
                            />
                            {errors.outcome && touched.outcome && (
                              <WarningText>{errors.outcome}</WarningText>
                            )}
                          </Column>
                        )}
                        <Column gap={5}>
                          <FieldLabel htmlFor='reason'>
                            {action === 'resolve'
                              ? 'O que foi conferido no extrato'
                              : 'Motivo do cancelamento'}
                          </FieldLabel>
                          <TextArea
                            id='reason'
                            name='reason'
                            value={values.reason}
                            placeholder='Descreva com pelo menos 10 caracteres'
                            onChange={event => setFieldValue ('reason', event.target.value)}
                            onBlur={() => setFieldTouched ('reason', true)}
                          />
                          {errors.reason && touched.reason && (
                            <WarningText>{errors.reason}</WarningText>
                          )}
                          <InfoText>
                            {'Fica no log de auditoria, e é o que responde "por que este repasse '
                            + 'terminou assim?" meses depois'}
                          </InfoText>
                        </Column>
                        <Actions>
                          <MainButton
                            label='Confirmar'
                            dangerous={action === 'cancel'}
                            onClick={handleSubmit}
                            disabled={isSubmitting}
                            loading={isSubmitting}
                            width={140}
                          />
                          <SecondaryButton
                            label='Voltar'
                            onClick={() => setAction (null)}
                            disabled={isSubmitting}
                            width={110}
                          />
                        </Actions>
                      </Section>
                    )}
                  </Formik>
                ) : (
                  <Actions>
                    {needsReview && (
                      <MainButton
                        label='Resolver'
                        leftIcon={<FaExclamationTriangle size={12} color='#252525'/>}
                        onClick={() => setAction ('resolve')}
                        width={150}
                      />
                    )}
                    {payout.cancelable && (
                      <SecondaryButton
                        label='Cancelar recebível'
                        onClick={() => setAction ('cancel')}
                        color='#FF3939'
                        width={180}
                      />
                    )}
                  </Actions>
                )}
              </>
            )}
          </Container>
        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}
